import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const VolunteerCode = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-emerald-700 hover:text-emerald-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Volunteer Code of Conduct</h1>
              <p className="text-gray-600">Delinam Foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardContent className="p-6 sm:p-8 lg:p-12">
                <div className="prose prose-gray max-w-none">
                  {/* Last Updated */}
                  <p className="text-sm text-gray-500 mb-8">
                    Last updated: February 17, 2026
                  </p>

                  {/* Introduction */}
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Delinam Foundation is committed to creating a respectful, safe, and inclusive 
                    environment for all volunteers, staff, partners, and community members.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-8">
                    By volunteering with Delinam Foundation, you agree to uphold the following standards:
                  </p>

                  {/* Respect & Inclusion */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Respect & Inclusion
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Volunteers must treat all individuals with dignity, fairness, and respect, regardless 
                      of background, identity, or beliefs. Discrimination, harassment, or abusive behavior 
                      will not be tolerated.
                    </p>
                  </div>

                  {/* Professional Conduct */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Professional Conduct
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Volunteers are expected to:
                    </p>
                    <ul className="list-none space-y-2 ml-0">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Act with integrity and honesty</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Follow all instructions and safety guidelines</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Respect confidentiality when handling sensitive information</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Represent Delinam Foundation positively in public and online spaces</span>
                      </li>
                    </ul>
                  </div>

                  {/* Safety & Wellbeing */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Safety & Wellbeing
                    </h2>
                    <p className="text-gray-700 mb-4">
                      Volunteers must:
                    </p>
                    <ul className="list-none space-y-2 ml-0">
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Follow safety procedures and risk protocols</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Immediately report unsafe conditions or incidents</span>
                      </li>
                      <li className="flex items-start text-gray-700">
                        <span className="text-emerald-600 mr-3 mt-1">•</span>
                        <span>Refrain from engaging in harmful or reckless behavior</span>
                      </li>
                    </ul>
                  </div>

                  {/* Protection of Vulnerable Individuals */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Protection of Vulnerable Individuals
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Any form of exploitation, abuse, or inappropriate conduct toward children or 
                      vulnerable individuals is strictly prohibited and will result in immediate 
                      removal and potential legal action.
                    </p>
                  </div>

                  {/* Use of Organization Property */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Use of Organization Property
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Volunteers must use Delinam Foundation resources responsibly and only for 
                      authorized purposes.
                    </p>
                  </div>

                  {/* Violations */}
                  <div className="mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-emerald-600">
                      Violations
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Delinam Foundation reserves the right to suspend or terminate volunteer participation 
                      at any time for conduct that violates this Code or poses a risk to others or the 
                      organization's mission.
                    </p>
                  </div>

                  {/* Acknowledgment */}
                  <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <CheckCircle className="w-6 h-6 mr-2 text-emerald-600" />
                      Acknowledgment
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      By participating in volunteer activities, you acknowledge and agree to comply 
                      with this Code of Conduct.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerCode;
